import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import api from '../../../app/api/api';
import MealItem from '../../../app/data/models/mealItem';
import CurrentPageItem from '../../../app/data/models/common/currentPageItem';

export interface KitchenMealItemsState {
  mealItems: MealItem[];
  nextPageId?: string;
  previousPageId?: string;
  status: string;
}

const initialState: KitchenMealItemsState = {
  mealItems: [],
  nextPageId: undefined,
  previousPageId: undefined,
  status: 'idle',
};

export const fetchKitchenMealItemsAsync = createAsyncThunk<
  CurrentPageItem<MealItem>,
  { kitchenId: string; nextPageId?: string; pageSize?: number }
>(
  'kitchenMealItems/fetchList',
  async ({ kitchenId, nextPageId, pageSize = 50 }, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (nextPageId !== undefined) {
        params.append('nextPageId', nextPageId.toString());
      }
      params.append('pageSize', pageSize.toString());

      const response = await api.KitchenStaffMealItem.list(
        kitchenId.toString(),
        params
      );
      return response;
    } catch (error) {
      if (error instanceof AxiosError) {
        return thunkAPI.rejectWithValue({ error: error.response?.data });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const updateMealItemAvailabilityAsync = createAsyncThunk<
  void,
  { kitchenId: string; mealItemId: string; isAvailable: boolean }
>(
  'kitchenMealItems/updateAvailability',
  async ({ kitchenId, mealItemId, isAvailable }, thunkAPI) => {
    try {
      await api.KitchenStaffMealItem.updateAvailability(
        kitchenId,
        mealItemId,
        isAvailable
      );
    } catch (error) {
      if (error instanceof AxiosError) {
        return thunkAPI.rejectWithValue({ error: error.response?.data });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const kitchenMealItemsSlice = createSlice({
  name: 'kitchenMealItems',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchKitchenMealItemsAsync.pending, (state) => {
        state.status = 'pendingFetchKitchenMealItems';
      })
      .addCase(fetchKitchenMealItemsAsync.fulfilled, (state, action) => {
        state.mealItems = action.payload.data;
        state.nextPageId = action.payload.nextPageId ?? undefined;
        state.previousPageId = action.payload.previousPageId ?? undefined;
        state.status = 'idle';
      })
      .addCase(fetchKitchenMealItemsAsync.rejected, (state) => {
        state.status = 'idle';
      })
      .addCase(updateMealItemAvailabilityAsync.pending, (state) => {
        state.status = 'pendingUpdateAvailability';
      })
      .addCase(updateMealItemAvailabilityAsync.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(updateMealItemAvailabilityAsync.rejected, (state) => {
        state.status = 'idle';
      });
  },
});

export default kitchenMealItemsSlice.reducer;
